/**
 * Logic THUẦN của thẻ nhân vật mới (đợt 260922 E2-P1) — tách khỏi file import antd để jest của react-scripts
 * chạy được (xem `promptChipUtils.ts`).
 */

interface LookLike { id: string; characterId: string; lookKey: string }

/** Bộ đồ của một nhân vật; bộ `default` (bộ chính) đứng đầu, các bộ khác giữ thứ tự server trả. */
export function looksOf<T extends LookLike>(characterId: string, all: T[]): T[] {
  const mine = all.filter((l) => l.characterId === characterId);
  const main = mine.filter((l) => l.lookKey === 'default');
  return [...main, ...mine.filter((l) => l.lookKey !== 'default')];
}

/** Bộ đang chọn trên thẻ. Id không còn (vừa bị xoá) hoặc chưa chọn ⇒ bộ đầu tiên. */
export function pickSelectedLook<T extends { id: string }>(looks: T[], selectedId?: string | null): T | null {
  if (!looks.length) return null;
  return looks.find((l) => l.id === selectedId) || looks[0];
}

/**
 * Tên trong kịch bản có trỏ vào hồ sơ này không — tên chính HOẶC tên phụ (260922 E1-P2: vai lặp "Guard" mang
 * "Guard 1", "Guard 2"). Không phân biệt hoa thường / khoảng trắng thừa.
 */
export function castNameMatches(c: { name: string; aliases?: string[] | null }, n: string): boolean {
  const k = String(n || '').trim().toLowerCase();
  if (!k) return false;
  return c.name.trim().toLowerCase() === k || (c.aliases || []).some((a) => String(a).trim().toLowerCase() === k);
}

export function faceUrlOf(c: { seedancePreviewUrl?: string | null; canonicalRefUrl?: string | null }): string | null {
  return c.seedancePreviewUrl || c.canonicalRefUrl || null;
}

/** Chip giới — giới là thứ neo giọng V.O. nên thiếu thì phải cảnh báo, không để trống im lặng. */
export type CharacterCardSummaryKey = 'kyc' | 'counts' | 'gender' | 'looks' | 'image' | 'name';
export type CharacterCardFoldedKey = 'appearance' | 'voice' | 'prompt' | 'scenes' | 'outfit' | 'alias' | 'stale';

/**
 * 261010 P9D D2 — gọn = ít bấm, không bớt nội dung. Summary luôn có KYC + số cảnh; folded giữ ngoại hình / giọng / prompt.
 * Tham số `c` giữ chữ ký để thẻ truyền nhân vật; luật hiện không phụ thuộc field (mọi hồ sơ cùng layout).
 */
export function characterCardFoldOf(_c?: { id?: string; name?: string } | null): {
  summary: CharacterCardSummaryKey[];
  foldedKeys: CharacterCardFoldedKey[];
} {
  return {
    summary: ['image', 'name', 'gender', 'looks', 'kyc', 'counts'],
    foldedKeys: ['appearance', 'outfit', 'scenes', 'voice', 'prompt', 'alias', 'stale'],
  };
}

/** Số CẢNH nhân vật có mặt (từ `FilmSceneLook[]` — ánh xạ nhân vật↔cảnh↔look, KHÔNG có video). */
export function characterCardCountsOf(sceneRows: Array<{ sceneId?: string | null }>): { scenes: number } {
  return { scenes: new Set(sceneRows.map((r) => r.sceneId).filter(Boolean)).size };
}

/**
 * Số VIDEO nhân vật xuất hiện, từ dữ liệu dùng thật của plan (`GET bank-usage` → `usage[]` theo `ownerId` = characterId):
 * đếm eventId khác nhau có bằng chứng VISIBLE (có `evidence` mà 0 VISIBLE ⇒ 0; chỉ dòng legacy không có `evidence` mới lấy `eventIds`). Chưa nạp được usage ⇒ null (card chỉ hiện số cảnh, không bịa 0).
 */
export function characterVideoCountOf(
  usage: { usage?: Array<{ ownerId: string; ownerType?: string; eventIds: string[]; evidence?: Array<{ eventId?: string; usage?: string }> }> } | null | undefined,
  characterId: string,
): number | null {
  if (!usage?.usage) return null;
  const rows = usage.usage.filter((r) => r.ownerId === characterId && (!r.ownerType || /CHAR/i.test(r.ownerType)));
  const ids = new Set<string>();
  for (const r of rows) {
    // Có evidence ⇒ CHỈ VISIBLE tính (kết quả lọc rỗng = «không xuất hiện», VOICE_ONLY / MENTIONED không đếm). Chỉ dòng legacy THIẾU hẳn evidence mới lấy `eventIds`.
    const evidence = Array.isArray(r.evidence) ? r.evidence : [];
    const list = evidence.length
      ? evidence.filter((e) => e?.usage === 'VISIBLE' && e.eventId).map((e) => String(e.eventId))
      : (r.eventIds ?? []);
    list.forEach((id) => ids.add(id));
  }
  return ids.size;
}

/** Chip «N video · M cảnh» (video lấy từ usage thật; null ⇒ chỉ «M cảnh»). */
export function characterCountLabelOf(sceneRows: Array<{ sceneId?: string | null }>, videoCount: number | null | undefined): string {
  const { scenes } = characterCardCountsOf(sceneRows);
  return typeof videoCount === 'number' && videoCount > 0 ? `${videoCount} video · ${scenes} cảnh` : `${scenes} cảnh`;
}

export function genderChip(gender?: string | null): { text: string; tone: 'ok' | 'warn' } {
  if (gender === 'female') return { text: '♀ Nữ', tone: 'ok' };
  if (gender === 'male') return { text: '♂ Nam', tone: 'ok' };
  return { text: '⚠ Chưa rõ giới — neo giọng V.O.', tone: 'warn' };
}

export interface NewLookHint {
  /** `main` = bộ mới sẽ là bộ chính · `from-main` = dựng từ ảnh bộ chính · `main-missing` = bộ chính chưa ảnh · `exists` = trùng tên. */
  kind: 'main' | 'from-main' | 'main-missing' | 'exists';
  text: string;
  /** Được phép bấm "Tạo và gen ảnh" không. */
  canGen: boolean;
}

/**
 * Dòng nhắc của form *Bộ đồ mới* (260922 E3-P4 — Add Look). Cùng luật với backend `pickSheetFace`: bộ phụ dựng từ ảnh bộ chính
 * (`default`, không có thì bộ cũ nhất — `looks` đã theo thứ tự server trả, cũ trước); bộ chính chưa ảnh ⇒ bộ phụ bị từ chối.
 * Tên trùng ⇒ server trả lại bộ cũ (`createLook` không tạo mới) ⇒ nói rõ, đừng hứa "bộ chính mới".
 */
export function newLookHint(looks: Array<{ id: string; lookKey: string; canonicalRefUrl?: string | null }>, newKey: string): NewLookHint {
  const key = String(newKey || '').trim().toLowerCase();
  const same = looks.find((l) => l.lookKey.toLowerCase() === key);
  if (key && same) return { kind: 'exists', text: `Đã có bộ "${same.lookKey}" — chọn bộ đó, không tạo mới.`, canGen: false };
  const main = looks.find((l) => l.lookKey === 'default') || looks[0];
  if (!main || key === 'default') return { kind: 'main', text: 'Đây sẽ là bộ chính — ảnh dựng từ nguồn mặt đã chọn (☺ Chọn mặt).', canGen: true };
  if (!main.canonicalRefUrl) return { kind: 'main-missing', text: `Bộ chính "${main.lookKey}" chưa có ảnh — gen bộ chính trước.`, canGen: false };
  return { kind: 'from-main', text: `Ảnh dựng từ bộ chính "${main.lookKey}" — giữ nguyên người, chỉ thay trang phục.`, canGen: true };
}
